import { z } from 'zod';
const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
// Reportes de CxC (2026-10-14): los meses y su tipo se comparten con la API y la web.
export const MESES_CXC = MESES;
export function siguientePeriodo(mes, anio) {
    const i = MESES.indexOf(mes);
    if (i === -1)
        throw new Error(`Mes inválido: ${mes}`);
    return i === MESES.length - 1 ? { mes: MESES[0], anio: anio + 1 } : { mes: MESES[i + 1], anio };
}
const MONTHS_EN = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
const MES_REGEX = new RegExp(`\\b(${[...MESES, ...MONTHS_EN].join('|')})\\b`, 'gi');
function conMismaCaja(original, reemplazo) {
    if (original === original.toUpperCase())
        return reemplazo.toUpperCase();
    if (original === original.toLowerCase())
        return reemplazo.toLowerCase();
    return reemplazo;
}
// "SERVICIO SEPTIEMBRE KOS MOOTO" → "SERVICIO OCTUBRE KOS MOOTO": cada nombre
// de mes (español o inglés, palabra completa) pasa al siguiente, respetando
// MAYÚSCULAS / minúsculas / Capitalizado. El resto del texto no se toca.
export function avanzarMesEnTexto(texto) {
    return texto.replace(MES_REGEX, (match) => {
        const lista = MESES.some(m => m.toLowerCase() === match.toLowerCase()) ? MESES : MONTHS_EN;
        const i = lista.findIndex(m => m.toLowerCase() === match.toLowerCase());
        return conMismaCaja(match, lista[(i + 1) % lista.length]);
    });
}
const AUTORIZACION_FACTURA = [
    'solicitar_factura', 'factura_solicitada', 'factura_cancelada',
    'nota_de_credito', 'esperar_autorizacion', 'factura_enviada',
    'factura_vigente', 'pago_atrasado',
];
// ── crearEntradaSchema ────────────────────────────────────────────────────────
export const crearEntradaSchema = z
    .object({
    cliente_id: z.string().uuid(),
    mes: z.enum(MESES),
    anio: z.coerce.number().int().min(2020).max(2100),
    fecha_factura: z.string().date().optional(),
    monto: z.coerce.number().positive(),
    moneda: z.enum(['MXN', 'USD', 'EUR']).default('MXN'),
    plazo_dias: z.coerce.number().int().positive().default(30),
    pago: z.enum(['', 'Si', 'No']).default(''),
    fecha_pago: z.string().date().optional(),
    estado_factura: z.enum(['solicitar', 'solicitada', 'emitida', 'cancelada']).default('solicitar'),
    numero_factura: z.string().max(50).optional(),
    xml_url: z.string().url().optional(),
    pdf_url: z.string().url().optional(),
    autorizacion_factura: z.enum(AUTORIZACION_FACTURA).default('solicitar_factura'),
    plan_accion: z.string().optional(),
    // Texto libre corto que viaja en la solicitud de factura al contador.
    concepto: z.string().trim().max(200).optional(),
    // #75 (2026-10-14): cobro adicional al servicio (refacturación, comisiones…),
    // descrito en Concepto. Separa los fees de servicio de lo adicional.
    es_adicional: z.boolean().default(false),
    // Fecha exacta prometida de pago (reemplaza "Paga en…" por mes).
    promesa_pago: z.string().date().optional(),
    comentarios: z.string().optional(),
})
    .superRefine((data, ctx) => {
    // F.6 §9 — con estado_factura='solicitar' la factura aún no existe, así
    // que fecha_factura es opcional; para cualquier otro estado (ya se
    // solicitó, se emitió o se canceló) la factura ya existe y su fecha es
    // obligatoria.
    if (data.estado_factura !== 'solicitar' && !data.fecha_factura) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fecha_factura'],
            message: 'fecha_factura es obligatoria cuando estado_factura no es "solicitar"',
        });
    }
});
// ── actualizarEntradaSchema ───────────────────────────────────────────────────
// estado_factura se gestiona exclusivamente via /solicitar-factura,
// /cancelar-factura y el webhook /recibir-factura — no via PATCH /:id.
export const actualizarEntradaSchema = z
    .object({
    mes: z.enum(MESES).optional(),
    anio: z.coerce.number().int().min(2020).max(2100).optional(),
    fecha_factura: z.string().date().nullable().optional(),
    monto: z.coerce.number().positive().optional(),
    moneda: z.enum(['MXN', 'USD', 'EUR']).optional(),
    plazo_dias: z.coerce.number().int().positive().optional(),
    pago: z.enum(['', 'Si', 'No']).optional(),
    fecha_pago: z.string().date().nullable().optional(),
    numero_factura: z.string().max(50).nullable().optional(),
    autorizacion_factura: z.enum(AUTORIZACION_FACTURA).optional(),
    plan_accion: z.string().nullable().optional(),
    concepto: z.string().trim().max(200).nullable().optional(),
    es_adicional: z.boolean().optional(),
    promesa_pago: z.string().date().nullable().optional(),
    comentarios: z.string().nullable().optional(),
})
    .superRefine((data, ctx) => {
    // Prompt: Fixes (eliminar catálogo, resolicitar factura, fecha de pago) —
    // "Cobrado este mes" en CxCPage.tsx ya depende de fecha_pago para saber a
    // qué mes atribuir el cobro; sin este resguardo, marcar pago='Si' sin
    // fecha lo dejaba fuera de ese cálculo en silencio. Solo dispara cuando el
    // payload trae pago='Si' explícito (partial update que no toca pago no se
    // ve afectado).
    if (data.pago === 'Si' && !data.fecha_pago) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fecha_pago'],
            message: 'fecha_pago es obligatoria cuando ¿Pagó? es "Sí"',
        });
    }
});
// ── recibirFacturaSchema ──────────────────────────────────────────────────────
export const recibirFacturaSchema = z.object({
    xml_url: z.string().url(),
    pdf_url: z.string().url(),
    // Fecha del comprobante (atributo Fecha de cfdi:Comprobante), parseada del
    // XML real — opcional porque el webhook n8n (recibir.ts) no la manda hoy;
    // factura-imap-poll.job.ts sí la manda siempre que logra parsear el XML.
    fecha_factura: z.string().date().optional(),
});
//# sourceMappingURL=cxc.js.map